
import supabase from '@/lib/supabaseClient';
import { apiCall } from '../services/api';
import { getActiveRegionConfig } from './regionConfig';
import { normalizeClientStatus } from './statusConfig';
import { hideSupersededJobs } from './supersededJobs';

// ── Companies ────────────────────────────────────────────


export async function getCompanies() {
  const response = await apiCall('/api/companies', { method: 'GET' });
  if (response.detail) throw new Error(response.detail);
  return response.map(mapCompanyFromDb);
}


export async function addCompany(company) {
  const regionConfig = getActiveRegionConfig();
  const identifierType = regionConfig.code === 'SG' ? 'uen' : (regionConfig.code === 'MY' ? 'registration_number' : 'companies_house_number');
  const payload = {
    name: company.name,
    registration_number: company.registrationNumber,
    uen: regionConfig.code === 'SG' ? company.registrationNumber : null,
    region_code: regionConfig.code,
    company_identifier_type: identifierType,
    company_type: company.companyType || null,
    year_end: company.yearEnd || null,
    accounts_due: company.accountsDue || null,
    confirmation_due: company.confirmationDue || null,
    registered_address: company.registeredAddress || null,
    sic_codes: company.sicCodes || null,
    status: company.status || 'active',
  };
  const response = await apiCall('/api/companies', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (response.detail) throw new Error(response.detail);
  return mapCompanyFromDb(response);
}


export async function deleteCompany(id) {
  const response = await apiCall(`/api/companies/${id}`, { method: 'DELETE' });
  if (response.detail) throw new Error(response.detail);
}

function mapCompanyFromDb(row) {
  return {
    id: row.id,
    name: row.name,
    registrationNumber: row.registration_number,
    uen: row.uen,
    regionCode: row.region_code,
    companyType: row.company_type,
    yearEnd: row.year_end,
    accountsDue: row.accounts_due,
    confirmationDue: row.confirmation_due,
    registeredAddress: row.registered_address,
    sicCodes: row.sic_codes,
    status: row.status,
    createdAt: row.created_at,
  };
}

// ── Jobs ─────────────────────────────────────────────────

export async function getJobs() {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return hideSupersededJobs(data.map(mapJobFromDb));
}

export async function getJob(jobId) {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('job_id', jobId)
    .single();
  if (error) throw error;
  return mapJobFromDb(data);
}

/**
 * Active jobs only (not filed/completed/archived/on_hold).
 * Used by the Dashboard stepper panel.
 */
export async function getActiveJobs() {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .not('client_status', 'in', '("filed","completed","archived","cancelled")')
    .order('created_at', { ascending: false })
    .limit(5);
  if (error) throw error;
  return data.map(mapJobFromDb);
}

/**
 * Filed/completed jobs only.
 * Used by the Filing History page.
 */
export async function getFilingHistory() {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .in('client_status', ['filed', 'completed', 'archived'])
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data.map(mapJobFromDb);
}

export async function createJobs(jobsArray) {
  const { data: { user } } = await supabase.auth.getUser();
  const rows = jobsArray.map(job => ({
    user_id: user.id,
    job_id: job.jobId,
    company_id: job.companyId || null,
    company_name: job.companyName,
    entity_name: job.entityName || job.companyName,
    services: job.services || [],
    account_type: job.accountType || null,
    financial_year: job.financialYear || null,
    service_type: job.serviceType || null,
    delivery_time: job.deliveryTime || null,
    payment_type: job.paymentType || null,
    stripe_session_id: job.stripeSessionId || null,
    file_name: job.fileName || null,
    estimated_delivery: job.estimatedDelivery || null,
    status: job.status || 'pending',
    status_history: job.statusHistory || [],
    date: job.date || new Date().toISOString().split('T')[0],
  }));

  const { data, error } = await supabase
    .from('jobs')
    .insert(rows)
    .select();
  if (error) throw error;
  return data.map(mapJobFromDb);
}

function mapJobFromDb(row) {
  const normalizedStatus = normalizeClientStatus(row.client_status || row.status);

  return {
    id: row.id,
    jobId: row.job_id,
    companyId: row.company_id,
    companyName: row.company_name,
    entityName: row.entity_name || row.company_name,
    companyNumber: row.company_number,
    services: row.services,
    regionCode: row.region_code,
    sourceSite: row.source_site,
    serviceProfile: row.service_profile,
    taxonomyProfile: row.taxonomy_profile,
    pricingProfile: row.pricing_profile,
    invoiceProfile: row.invoice_profile,
    workflowProfile: row.workflow_profile,
    companyIdentifierType: row.company_identifier_type,
    uen: row.uen,
    accountType: row.account_type,
    financialYear: row.financial_year,
    serviceType: row.service_type,
    deliveryTime: row.delivery_time,
    paymentType: row.payment_type,
    stripeSessionId: row.stripe_session_id,
    fileName: row.file_name,
    estimatedDelivery: row.estimated_delivery,
    // Prefer client_status (synced by Provider Portal) and normalize legacy aliases.
    status: normalizedStatus,
    clientStatus: row.client_status,
    providerStatus: row.provider_status,
    statusHistory: row.status_history,
    date: row.date,
    createdAt: row.created_at,
    // Filing tracking
    chFilingStatus: row.ch_filing_status,
    hmrcFilingStatus: row.hmrc_filing_status,
    // Rating
    ratingStars: row.rating_stars,
    ratingComment: row.rating_comment,
    ratedAt: row.rated_at,
    // Archival
    isArchived: row.is_archived,
    // iXBRL metadata
    accountingFramework: row.accounting_framework,
    taxonomyVersion: row.taxonomy_version,
    entityType: row.entity_type,
    periodStart: row.period_start,
    periodEnd: row.period_end,
    onHoldReason: row.on_hold_reason,
    // Payment mode fields
    paymentMode: row.payment_mode,
    paymentStatus: row.payment_status,
    creditStatus: row.credit_status,
    deliveryLocked: row.delivery_locked,
    paymentDueDate: row.payment_due_date,
    // tag-fsh integration (set by Provider Portal's webhook receiver once
    // the job has been sent to/tagged by tag-fsh)
    tagfshStatus: row.tagfsh_status,
    // tag-fsh v2 integration (synced from the Provider Portal webhook path)
    tagfshV2Status: row.tagfsh_v2_status,
    tagfshV2WorkstreamId: row.tagfsh_v2_workstream_id,
    tagfshV2UpdatedAt: row.tagfsh_v2_updated_at,
    tagfshV2PackageHash: row.tagfsh_v2_package_hash,
  };
}

// ── Wizard Progress ──────────────────────────────────────

export async function getWizardProgress() {
  const { data, error } = await supabase
    .from('wizard_progress')
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data ? { ...data.data, step: data.step, savedAt: data.saved_at } : null;
}

export async function saveWizardProgress(wizardData) {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('wizard_progress')
    .upsert({
      user_id: user.id,
      data: wizardData,
      step: wizardData.step || 1,
      saved_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });
  if (error) throw error;
}

export async function clearWizardProgress() {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('wizard_progress')
    .delete()
    .eq('user_id', user.id);
  if (error) throw error;
}
